import { useState } from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import { BO_MAX_RETRIES, type PositionDetail } from '@/types/trades.types';
import { formatDate } from '@/utils/format';
import { isTradeDateInFuture } from '@/utils/trades/trade-date';
import {
  LEGACY_BO_ERROR_CAPTION,
  mayBeBookedAtBackOffice,
  resolveBoReason,
} from '@/utils/trades/bo-posting-copy';

interface TradePendingBannerProps {
  trade: PositionDetail;
  /** Open the Amend modal (the discoverable path to change the settlement date). */
  onOpenAmend: () => void;
}

/**
 * LR-544: explainability for the "Pending Trade Date" state on the trade-detail
 * page. Renders one of three variants (else `null`):
 *
 *  - rejected                         → red: BO gave up after max retries.
 *  - pending with BO retries (>0)     → red: BO hold failed, retry N/max, hourly.
 *  - clean pending (no retries)       → neutral plaque: what happens next +
 *                                       how to change the settlement date (Amend).
 *
 * Owns its own max-width container so the page can drop it in unconditionally
 * (it self-hides for every other status).
 *
 * LR-712 split what this says from what BoPostingPanel says. The banner explains
 * the trade's STATUS and the operator's move; the panel below explains what
 * BackOffice did with the posting and shows its verbatim response. Before the
 * split both rendered on the same rejected trade, each with its own `<pre>` — and
 * they could disagree, because this one printed `last_bo_error`, the single
 * overwritable column that names whichever posting failed last rather than the one
 * that stalled the trade. The blob is kept here only when there is no posting
 * record at all (a trade rejected before LR-709), where nothing contradicts it.
 */
export function TradePendingBanner({ trade, onOpenAmend }: TradePendingBannerProps) {
  const retryCount = trade.bo_retry_count ?? 0;
  // The legacy string comes back non-empty only in the absence of a posting
  // record — see the note above.
  const { posting, legacy: lastError } = resolveBoReason(trade);
  // The guard is holding this trade's postings: nothing is being retried, and
  // "fix the data and Retry" is the wrong instruction — a repost is exactly what
  // must not happen until BackOffice's answer is known.
  const blockedAtBo = posting != null && mayBeBookedAtBackOffice(posting);
  const isRejected = trade.status === 'rejected';
  const isPendingWithRetries = trade.status === 'pending' && retryCount > 0;
  const isCleanPending = trade.status === 'pending' && retryCount === 0;
  // A future Trade Date is the canonical clean-pending case: "Open now" is
  // disabled (opens automatically on TD), so the copy must NOT point at it.
  const isFutureTd = isTradeDateInFuture(trade.trade_date);

  if (!isRejected && !isPendingWithRetries && !isCleanPending) return null;

  return (
    <div className="mx-auto max-w-[1440px] px-6 pt-4">
      {isRejected && (
        <BoErrorBanner
          title="Rejected by BackOffice"
          detail={
            blockedAtBo
              ? `The BackOffice hold failed ${retryCount} time${retryCount === 1 ? '' : 's'} and the trade was rejected. Do NOT retry it yet: an earlier posting may already be booked in BackOffice — settle that first, using the posting panel below.`
              : `The BackOffice hold failed ${retryCount} time${retryCount === 1 ? '' : 's'} and the trade was rejected. Fix the underlying BO reference data (e.g. the counterparty account), then use Retry to re-queue it.`
          }
          error={lastError}
        />
      )}

      {isPendingWithRetries && (
        <BoErrorBanner
          title="BackOffice hold failed"
          detail={
            blockedAtBo
              ? `Retry ${retryCount} of ${BO_MAX_RETRIES}. It is NOT being retried on a schedule: an earlier posting never confirmed, so further postings for this trade are held until that is settled — see the posting panel below.`
              : `Retry ${retryCount} of ${BO_MAX_RETRIES} — the hold is retried automatically every hour. You can also fix the BO reference data and use “Open now” to retry immediately.`
          }
          error={lastError}
        />
      )}

      {isCleanPending && (
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
          role="note"
        >
          <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-none text-blue-500" aria-hidden="true" />
          <div className="min-w-0 text-sm text-blue-900">
            <p className="font-medium">Booking on Trade Date</p>
            {isFutureTd ? (
              <p className="mt-0.5 text-blue-800">
                This trade opens automatically on its Trade Date ({formatDate(trade.trade_date)}) —
                the <strong>Settle</strong> action appears once the BackOffice hold is placed. To
                bring it forward, change the settlement date via Amend.
              </p>
            ) : (
              <p className="mt-0.5 text-blue-800">
                This trade opens on its Trade Date — the <strong>Settle</strong> action appears
                only once the BackOffice hold is placed. Use <strong>Open now</strong> to fire the
                Trade Date action immediately, or change the settlement date via Amend.
              </p>
            )}
            <button
              type="button"
              onClick={onOpenAmend}
              className="mt-2 inline-flex items-center rounded border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
            >
              Amend settlement date
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface BoErrorBannerProps {
  title: string;
  detail: string;
  error: string;
}

/** Red banner: title + explanation + the legacy BO error (scrollable, copyable).
 *
 * LR-712: `error` now only ever carries the pre-LR-709 `last_bo_error` string —
 * a trade with a posting record shows BackOffice's verbatim response in the
 * BoPostingPanel instead, attached to the posting it actually belongs to. So the
 * block is captioned: this text could not name its own posting, and must not be
 * read as though it could. */
function BoErrorBanner({ title, detail, error }: BoErrorBannerProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!error) return;
    navigator.clipboard?.writeText(error).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => { /* clipboard denied — no-op, the text is visible inline anyway */ },
    );
  };

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none text-red-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-red-800">{title}</p>
            {error && (
              <button
                type="button"
                onClick={handleCopy}
                className="flex-none rounded border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
              >
                {copied ? 'Copied' : 'Copy error'}
              </button>
            )}
          </div>
          <p className="mt-0.5 text-sm text-red-700">{detail}</p>
          {error && (
            <>
              {/* LR-544 r2 (kvi P8): focusable + labelled so a keyboard user can
                  reach and scroll the (up to 2000-char) BO error, not just a mouse. */}
              <pre
                tabIndex={0}
                role="region"
                aria-label="BackOffice error detail"
                className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap break-all rounded border border-red-200 bg-white p-2 text-[11px] font-mono text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                {error}
              </pre>
              <p className="mt-1 text-[11px] italic text-red-700/80">
                {LEGACY_BO_ERROR_CAPTION}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
